import { EntityMetadataModel, EnumItemMetadataModel, EnumMetadataModel, PrimitiveType } from '../../../api/api';
import { JSON_SCHEMA_FORMATS, JSON_SCHEMA_TYPES, JsonSchemaModel } from '../Models/JsonSchemaModel';
import { ObjectPropertyType } from '../Models/ObjectPropertyType';
import { ObjectSchemaType } from '../Models/ObjectSchemaType';
import { getNonNullSchema, getSchemaAlternatives, schemaAllowsNull } from './JsonSchemaTypes';

export function getJsonSchemaFromProperty(property: ObjectPropertyType): JsonSchemaModel {
	if (property.jsonSchema) return property.jsonSchema;
	let schema: JsonSchemaModel;
	if (property.referenceEntity) {
		const entity = property.referenceEntity as EntityMetadataModel & ObjectSchemaType;
		schema = {
			type: 'object',
			title: entity.name,
			properties: Object.fromEntries(entity.properties.map((p) => [p.name, getJsonSchemaFromProperty(p)])),
			required: entity.properties.filter((p) => p.isRequired).map((p) => p.name),
			additionalProperties: entity.additionalProperties ? getJsonSchemaFromProperty(entity.additionalProperties) : entity.allowsAdditionalProperties,
		};
	} else {
		const type =
			property.type === PrimitiveType.Boolean
				? 'boolean'
				: property.type === PrimitiveType.Integer
					? 'integer'
					: property.type === PrimitiveType.Decimal
						? 'number'
						: property.type === PrimitiveType.Object
							? 'object'
							: 'string';
		schema = { type };
	}
	return property.isMultiple ? { type: 'array', items: schema } : schema;
}

/**
 * Converts a JsonSchemaModel to EntityMetadataModel format
 */
export function getEntityMetadataModelFromJsonSchema(jsonSchema: JsonSchemaModel): EntityMetadataModel {
	jsonSchema = getNonNullSchema(jsonSchema);
	if (jsonSchema.anyOf || jsonSchema.oneOf) {
		const branches = getSchemaAlternatives(jsonSchema);
		const commonNames = Object.keys(branches[0]?.properties ?? {}).filter((name) => branches.every((branch) => branch.properties?.[name]));
		jsonSchema = {
			title: jsonSchema.title,
			type: 'object',
			properties: Object.fromEntries(commonNames.map((name) => [name, { anyOf: branches.map((branch) => branch.properties![name]) }])),
			required: commonNames.filter((name) => branches.every((branch) => branch.required?.includes(name))),
			additionalProperties: false,
		};
	}
	const properties: any[] = [];

	if (jsonSchema.properties) {
		for (const [propName, propSchema] of Object.entries(jsonSchema.properties)) {
			const isRequired = jsonSchema.required?.includes(propName) ?? false;
			const property = convertJsonSchemaPropertyToEntityProperty(propName, propSchema, isRequired);
			properties.push(property);
		}
	}

	return {
		id: jsonSchema.$id || '',
		name: jsonSchema.title || 'Schema',
		description: jsonSchema.description,
		properties: properties,
		allowsAdditionalProperties: jsonSchema.additionalProperties !== false,
		additionalProperties:
			jsonSchema.additionalProperties && typeof jsonSchema.additionalProperties === 'object'
				? convertJsonSchemaPropertyToEntityProperty('$value', jsonSchema.additionalProperties, false)
				: undefined,
	} as EntityMetadataModel & ObjectSchemaType;
}

/**
 * Maps a JSON schema type and format to a PrimitiveType
 */
function mapJsonSchemaTypeAndFormatToPrimitiveType(schema: JsonSchemaModel): PrimitiveType {
	switch (schema.type) {
		case JSON_SCHEMA_TYPES.STRING:
			// Check format to map to appropriate PrimitiveType
			if (schema.format === JSON_SCHEMA_FORMATS.UUID) {
				return PrimitiveType.Guid;
			} else if (schema.format === JSON_SCHEMA_FORMATS.DATE_TIME) {
				return PrimitiveType.DateTime;
			} else if (schema.format === JSON_SCHEMA_FORMATS.DATE) {
				return PrimitiveType.Date;
			} else {
				return PrimitiveType.String;
			}
		case JSON_SCHEMA_TYPES.NUMBER:
			return PrimitiveType.Decimal;
		case JSON_SCHEMA_TYPES.INTEGER:
			return PrimitiveType.Integer;
		case JSON_SCHEMA_TYPES.BOOLEAN:
			return PrimitiveType.Boolean;
		case JSON_SCHEMA_TYPES.OBJECT:
			return PrimitiveType.Object;
		default:
			return PrimitiveType.Object;
	}
}

/**
 * Maps a JSON schema type to primitive type with metadata
 */
function mapJsonSchemaTypeToPrimitive(schema: JsonSchemaModel): {
	primitiveType: PrimitiveType;
	isComplex: boolean;
	referenceEntity?: EntityMetadataModel;
	referenceEnum?: EnumMetadataModel;
} {
	let primitiveType: PrimitiveType;
	let isComplex = false;
	let referenceEntity: EntityMetadataModel | undefined;
	let referenceEnum: EnumMetadataModel | undefined;

	// Check if this is an enum (has enum values defined)
	if (schema.enum && schema.enum.length > 0) {
		const enumRef = schema['x-enumRef'];
		// Map the schema base type to PrimitiveType
		primitiveType = mapJsonSchemaTypeAndFormatToPrimitiveType(schema);
		// Create an inline enum metadata from the enum values
		referenceEnum = {
			id: enumRef?.id || schema.$id || '',
			name: enumRef?.name || schema.title || 'Enum',
			description: schema.description || '',
			items: schema.enum.map(
				(value, index) =>
					({
						id: `${index}`,
						name: String(value),
						value: typeof value === 'number' ? value : index,
					}) as EnumItemMetadataModel,
			),
		};
		return { primitiveType, isComplex, referenceEntity, referenceEnum };
	}

	// Map the base type
	primitiveType = mapJsonSchemaTypeAndFormatToPrimitiveType(schema);
	if (schema.anyOf || schema.oneOf) {
		const alternatives = getSchemaAlternatives(schema);
		if (alternatives.length && alternatives.every((branch) => branch.type === alternatives[0].type && branch.format === alternatives[0].format)) {
			primitiveType = mapJsonSchemaTypeAndFormatToPrimitiveType(alternatives[0]);
		}
	}

	// Handle complex types
	if (schema.type === JSON_SCHEMA_TYPES.OBJECT) {
		isComplex = true;
		// Recursively convert nested object schema
		if (schema.properties || schema.additionalProperties) {
			referenceEntity = getEntityMetadataModelFromJsonSchema(schema);
		}
	}
	if ((schema.anyOf || schema.oneOf) && getSchemaAlternatives(schema).every((branch) => branch.type === 'object')) {
		isComplex = true;
		referenceEntity = getEntityMetadataModelFromJsonSchema(schema);
	}

	return { primitiveType, isComplex, referenceEntity, referenceEnum };
}

/**
 * Converts a single JSON schema property to an entity property with support for nested objects
 */
export function convertJsonSchemaPropertyToEntityProperty(propName: string, propSchema: JsonSchemaModel, isRequired: boolean): ObjectPropertyType {
	const originalSchema = propSchema;
	propSchema = getNonNullSchema(propSchema);
	let primitiveType: PrimitiveType;
	let referenceEntity: EntityMetadataModel | undefined;
	let referenceEnum: EnumMetadataModel | undefined;
	let isMultiple = false;
	let isComplex = false;

	if (propSchema.type === JSON_SCHEMA_TYPES.ARRAY) {
		isMultiple = true;
		// For arrays, we need to determine the item type
		if (propSchema.items && !Array.isArray(propSchema.items)) {
			const typeInfo = mapJsonSchemaTypeToPrimitive(propSchema.items);
			primitiveType = typeInfo.primitiveType;
			isComplex = typeInfo.isComplex;
			referenceEntity = typeInfo.referenceEntity;
			referenceEnum = typeInfo.referenceEnum;
		} else {
			primitiveType = PrimitiveType.String;
		}
	} else {
		const typeInfo = mapJsonSchemaTypeToPrimitive(propSchema);
		primitiveType = typeInfo.primitiveType;
		isComplex = typeInfo.isComplex;
		referenceEntity = typeInfo.referenceEntity;
		referenceEnum = typeInfo.referenceEnum;
	}
	const isDictionary = !!(referenceEntity as (EntityMetadataModel & ObjectSchemaType) | undefined)?.additionalProperties;

	return {
		jsonSchema: originalSchema,
		name: propName,
		type: primitiveType,
		isRequired: isRequired,
		isMultiple: isMultiple,
		isComplex: isComplex,
		description: propSchema.description,
		referenceEntity: schemaAllowsNull(originalSchema) && !isDictionary ? undefined : referenceEntity,
		referenceEnum: referenceEnum,
	} as ObjectPropertyType;
}
